import React from 'react';
import { Loader } from 'react-feather';
import { useNavigate } from 'react-router-dom';

import { UserListTableProps } from '../../types/userTypes';

const UserListTable: React.FC<UserListTableProps> = ({
  users,
  isLoading,
  hasError,
  isEmpty,
  error,
}) => {
  const navigate = useNavigate();

  const moveToDetail = (uuid: string) => {
    navigate(`/users/${uuid}`);
  };

  if (hasError) {
    const errorMessage =
      error instanceof Error ? error.message : 'An unknown error occurred';
    alert(errorMessage);
    return (
      <div className="scrollbar-custom h-[88%] w-full overflow-x-auto border border-gray-300">
        <div className="flex h-full items-center justify-center bg-white">
          <p className="text-gray-500">An unknown error occurred</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="scrollbar-custom w-full overflow-x-auto border border-gray-300">
        <div className="flex items-center justify-center bg-white py-5">
          <Loader className="h-6 w-6 animate-spin text-gray-500" />
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="scrollbar-custom w-full overflow-x-auto border border-gray-300">
        <div className="flex items-center justify-center bg-white py-5">
          <p className="text-gray-500">No search results found.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="scrollbar-custom h-[88%] w-full overflow-x-auto border border-gray-300"
      role="region"
      aria-labelledby="user-list-title"
    >
      <table
        className="w-full min-w-[1320px] table-fixed border-collapse bg-white"
        aria-describedby="user-list-title"
      >
        <thead>
          <tr>
            <th
              scope="col"
              className="sticky top-0 h-9 w-[220px] whitespace-nowrap border-b border-gray-300 bg-[#EFEFEF] px-5 py-2.5 text-left text-sm font-medium text-[#707070]"
            >
              Company
            </th>
            <th
              scope="col"
              className="sticky top-0 h-9 w-[180px] whitespace-nowrap border-b border-gray-300 bg-[#EFEFEF] px-5 py-2.5 text-left text-sm font-medium text-[#707070]"
            >
              Representative Name
            </th>
            <th
              scope="col"
              className="sticky top-0 h-9 w-[260px] whitespace-nowrap border-b border-gray-300 bg-[#EFEFEF] px-5 py-2.5 text-left text-sm font-medium text-[#707070]"
            >
              Email
            </th>
            <th
              scope="col"
              className="sticky top-0 h-9 w-[240px] whitespace-nowrap border-b border-gray-300 bg-[#EFEFEF] px-5 py-2.5 text-left text-sm font-medium text-[#707070]"
            >
              Business Registration Number
            </th>
            <th
              scope="col"
              className="sticky top-0 h-9 w-[260px] whitespace-nowrap border-b border-gray-300 bg-[#EFEFEF] px-5 py-2.5 text-left text-sm font-medium text-[#707070]"
            >
              Business Address
            </th>
            <th
              scope="col"
              className="sticky top-0 h-9 w-[160px] whitespace-nowrap border-b border-gray-300 bg-[#EFEFEF] px-5 py-2.5 text-left text-sm font-medium text-[#707070]"
            >
              Email Subscription
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.uuid}>
              <td
                className="h-9 whitespace-nowrap border-b border-gray-300 bg-white px-5 py-2.5 text-left text-sm font-normal text-[#1D1D1D] hover:cursor-pointer hover:text-gray-500 hover:underline"
                onClick={() => moveToDetail(user.uuid)}
                tabIndex={0}
                role="button"
                aria-label={`View details for ${user.companyName}`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    moveToDetail(user.uuid);
                  }
                }}
              >
                {user.companyName}
              </td>
              <td className="h-9 whitespace-nowrap border-b border-gray-300 bg-white px-5 py-2.5 text-left text-sm font-normal text-[#1D1D1D]">
                {user.representativeName}
              </td>
              <td className="h-9 whitespace-nowrap border-b border-gray-300 bg-white px-5 py-2.5 text-left text-sm font-normal text-[#1D1D1D]">
                {user.email}
              </td>
              <td className="h-9 whitespace-nowrap border-b border-gray-300 bg-white px-5 py-2.5 text-left text-sm font-normal text-[#1D1D1D]">
                {user.businessRegistrationNumber}
              </td>
              <td className="h-9 whitespace-nowrap border-b border-gray-300 bg-white px-5 py-2.5 text-left text-sm font-normal text-[#1D1D1D]">
                {user.companyAddress}
              </td>
              <td className="h-9 whitespace-nowrap border-b border-gray-300 bg-white px-5 py-2.5 text-left text-sm font-normal text-[#1D1D1D]">
                <span
                  className={`inline-block h-2 w-2 rounded-full ${
                    user.marketingAgree ? 'bg-green-500' : 'bg-gray-500'
                  }`}
                  aria-label={
                    user.marketingAgree
                      ? 'Subscribed to email'
                      : 'Not subscribed to email'
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserListTable;
